import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAdmin } from '../../contexts/AdminContext';
import { formatDateTime } from '../../utils/timezoneUtils';

interface DashboardStats {
  totalPackages: number;
  activePackages: number;
  totalCountries: number;
  totalRequests: number;
  totalTickets: number;
  pendingTickets: number;
}

const AdminDashboard: React.FC = () => {
  const { admin } = useAdmin();
  const [stats, setStats] = useState<DashboardStats>({
    totalPackages: 0,
    activePackages: 0,
    totalCountries: 0,
    totalRequests: 0,
    totalTickets: 0,
    pendingTickets: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      
      // Fetch packages count
      const packagesResponse = await fetch('/backend/api/admin/packages/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const packagesData = await packagesResponse.json();
      
      // Fetch countries count
      const countriesResponse = await fetch('/backend/api/admin/countries/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const countriesData = await countriesResponse.json();

      // Fetch tickets stats
      const ticketsResponse = await fetch('https://patrixglobal.com/backend/api/admin/tickets/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const ticketsData = await ticketsResponse.json();

      if (packagesData.packages && countriesData.countries) {
        const tickets = ticketsData.success ? ticketsData.tickets : [];
        setStats({
          totalPackages: packagesData.packages.length,
          activePackages: packagesData.packages.filter((pkg: any) => pkg.active).length,
          totalCountries: countriesData.countries.length,
          totalRequests: 0, // Will be implemented when requests API is ready
          totalTickets: tickets.length,
          pendingTickets: tickets.filter((ticket: any) => 
            ticket.status === 'waiting_for_client' || ticket.status === 'processing'
          ).length
        });
      }
    } catch (error) {    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Packages',
      value: stats.totalPackages,
      icon: '📦',
      bgColor: 'bg-blue-500',
      textColor: 'text-blue-600',
      bgLight: 'bg-blue-50'
    },
    {
      title: 'Active Packages',
      value: stats.activePackages,
      icon: '✅',
      bgColor: 'bg-green-500',
      textColor: 'text-green-600',
      bgLight: 'bg-green-50'
    },
    {
      title: 'Countries',
      value: stats.totalCountries,
      icon: '🌍',
      bgColor: 'bg-purple-500',
      textColor: 'text-purple-600',
      bgLight: 'bg-purple-50'
    },
    {
      title: 'Client Requests',
      value: stats.totalRequests,
      icon: '📋',
      bgColor: 'bg-orange-500',
      textColor: 'text-orange-600',
      bgLight: 'bg-orange-50'
    },
    {
      title: 'Support Tickets',
      value: stats.totalTickets,
      icon: '🎫',
      bgColor: 'bg-indigo-500',
      textColor: 'text-indigo-600',
      bgLight: 'bg-indigo-50'
    },
    {
      title: 'Pending Tickets',
      value: stats.pendingTickets,
      icon: '⏳',
      bgColor: 'bg-red-500',
      textColor: 'text-red-600',
      bgLight: 'bg-red-50'
    }
  ];

  return (
    <AdminLayout>
      <div className="space-y-6" dir="ltr">
        {/* Welcome Section */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Welcome back, {admin?.full_name}!
          </h2>
          <p className="text-gray-600">
            Here's what's happening with your company formation services today.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {statCards.map((card, index) => (
            <div key={index} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">
                    {card.title}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {loading ? (
                      <div className="animate-pulse bg-gray-200 h-8 w-12 rounded"></div>
                    ) : (
                      card.value
                    )}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${card.bgLight}`}>
                  <span className="text-2xl">{card.icon}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <a
                href="/admin/packages"
                className="flex items-center p-3 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <span className="mr-3 text-lg">➕</span>
                Add New Package
              </a>
              <a
                href="/admin/packages"
                className="flex items-center p-3 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <span className="mr-3 text-lg">📝</span>
                Manage Existing Packages
              </a>
              <a
                href="/admin/countries"
                className="flex items-center p-3 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <span className="mr-3 text-lg">🌍</span>
                Manage Countries
              </a>
              <a
                href="/admin/requests"
                className="flex items-center p-3 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <span className="mr-3 text-lg">👥</span>
                View Client Requests
              </a>
              <a
                href="/admin/tickets"
                className="flex items-center p-3 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <span className="mr-3 text-lg">🎫</span>
                Manage Support Tickets
              </a>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">System Info</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Your Role:</span>
                <span className="font-medium capitalize text-gray-900">
                  {admin?.role?.replace('_', ' ')}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Last Login:</span>
                <span className="font-medium text-gray-900">
                  {formatDateTime(new Date(), { year: 'numeric', month: 'short', day: 'numeric' }, 'en-US')}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Status:</span>
                <span className="font-medium text-green-600">Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;